import { environment } from '@env';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { NotificationEvent } from '../../interface/notifications-event.interface';
import { ReconciliationRequest } from '../../models/reconciliation-request.class';
import { Record } from '../../models/record/record.class';

import { getAllTransformationsServicesMap$ } from './transformations.selector';
import {
    getAppsMetaDataMap$,
    getAppsTemplate$,
    getAssetsWithLatestVersion$,
    getBridgeNodesMap$,
    getPackagedAppsMap$,
    getSubscribedAppsMap$,
    transformMetaDataWithTemplate,
} from './apps.selector';
import { getCurrentOrganizationId$ } from './session.selector';
import { getNotificationsMap$ } from './notifications.selector';
import { getReportsMap$, getSearchReportsOrgMapByCurrentOrgId$ } from './reports.selector';
import { getRequests$, getRequestsOrgServicesMap$ } from './requests.selector';
import { getWorkflowInstances$, getWorkflowsLoadingMap$ } from './workflow.selector';
import { AppTemplate } from '../../models/app-template.class';
import { AssetData } from '../../models/assetdata.class';
import { AssetService } from '../../models/assetservice.class';
import { Instance } from '../../models/instance.interface';
import { transformAppMetaData } from '../constants/configuration.constant';
import { ENINVOICE_REDUCER_KEY } from '../reducers/eninvoice.reducer';
import { AppsState } from '../states/apps.state';
import { EninvoiceState } from '../states/eninvoice.state';
import { getInstancesOrgServicesMap$, getServicesInstancesMap$ } from './instances.selector';

const currentState = createFeatureSelector<EninvoiceState>(ENINVOICE_REDUCER_KEY);

export const getEninvoiceSelectedPackageRestApiName$ = createSelector(currentState, state => state.selectedPackageRestApiName);

export const getEninvoiceSelectedBridgeRestApiName$ = createSelector(currentState, state => state.selectedBridgeRestApiName);

export const getEninvoiceSelectedAppRestApiName$ = createSelector(currentState, state => state.selectedAppRestApiName);

export const getEninvoiceSelectedReportName$ = createSelector(currentState, state => state.selectedReportName);

export const getEninvoiceSelectedInstanceId$ = createSelector(currentState, state => state.selectedInstanceId);

export const getEninvoiceSelectedRequestId$ = createSelector(currentState, state => state.selectedRequestId);

export const getSelectedRequestLogName$ = createSelector(currentState, state => state.selectedRequestLogName || 'eninvoice');

export const getEnInvoiceApps$ = createSelector(getSubscribedAppsMap$, appsMap => {
    return Object.keys(appsMap)
        ?.filter(serviceId => checkIfEninvoiceApp(appsMap[serviceId]))
        ?.reduce((list, id) => {
            appsMap[id] && list.push(appsMap[id]);
            return list;
        }, [] as AssetService[])
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName));
});

export const getEninvoiceSubscribedAppsApiMap$ = createSelector(
    getEnInvoiceApps$,
    getPackagedAppsMap$,
    getBridgeNodesMap$,
    getAssetsWithLatestVersion$,
    (apps, appPackagesMap, bridgesMap, version) => {
        return {
            ...(apps.reduce((appsMap, app) => {
                appsMap[app.restApiName] = app;
                return appsMap;
            }, {}) || {}),
            ...Object.keys(appPackagesMap || {}).reduce((packagesMap, packageAppMetaUId) => {
                const packageApp = apps?.find(
                    service => service.assetMetaUId || version?.[service.serviceId]?.metadataId === packageAppMetaUId
                );
                Object.keys(appPackagesMap[packageAppMetaUId] || {}).forEach(nodeId => {
                    const node = appPackagesMap[packageAppMetaUId][nodeId];
                    if (!node.restApiName) {
                        return;
                    }
                    packagesMap[node.restApiName] = JSON.parse(JSON.stringify(node));
                    packagesMap[node.restApiName].package = packageApp;
                    packagesMap[node.restApiName].assetMetaUId = node.id;
                });
                return packagesMap;
            }, {}),
            ...Object.keys(bridgesMap || {}).reduce((nodesMap, bridgeAppMetaUId) => {
                const bridgeApp = apps?.find(
                    service => service.assetMetaUId || version?.[service.serviceId]?.metadataId === bridgeAppMetaUId
                );
                Object.keys(bridgesMap[bridgeAppMetaUId] || {}).forEach(nodeId => {
                    const node = bridgesMap[bridgeAppMetaUId][nodeId];
                    if (!node.restApiName) {
                        return;
                    }
                    nodesMap[node.restApiName] = JSON.parse(JSON.stringify(node));
                    nodesMap[node.restApiName].bridge = bridgeApp;
                    nodesMap[node.restApiName].assetMetaUId = node.id;
                });
                return nodesMap;
            }, {}),
        } as { [property: string]: AssetService };
    }
);

const checkIfEninvoiceApp = (app: AssetService) => {
    if (environment.restrictedProcessingUIs.includes('enreport')) {
        return true;
    }
    return app?.tags?.find(
        tag => (tag.tagKey === 'uitype' && tag.tagValue === 'eninvoice') || (tag.tagKey === 'package' && tag.tagValue === 'ewb')
    );
};

export const getEninvoiceSelectedPackageApp$ = createSelector(
    getEninvoiceSubscribedAppsApiMap$,
    getEninvoiceSelectedPackageRestApiName$,
    (map, api) => map?.[api]
);

export const getEninvoicePackagedApps$ = createSelector(
    getEninvoiceSelectedPackageApp$,
    getPackagedAppsMap$,
    (packageApp, map) => map?.[packageApp?.serviceId]
);

export const getEninvoiceSelectedBridgeApp$ = createSelector(
    getEninvoiceSubscribedAppsApiMap$,
    getEninvoiceSelectedBridgeRestApiName$,
    getAssetsWithLatestVersion$,
    (map, api, versions) => {
        const metaId = versions?.[map?.[api]?.serviceId]?.metadataId;
        return metaId
            ? {
                  ...map?.[api],
                  assetMetaUId: metaId,
              }
            : map?.[api];
    }
);

const getEninvoiceSelectedBridgeNode$ = createSelector(
    getEninvoiceSelectedBridgeApp$,
    getEninvoiceSelectedAppRestApiName$,
    getBridgeNodesMap$,
    getAssetsWithLatestVersion$,
    (bridge, subAppApi, nodesMap, version) => {
        if (!(bridge?.assetMetaUId?.length > 0 && subAppApi?.length > 0)) {
            return;
        }
        const nodes = nodesMap?.[bridge.assetMetaUId || version?.[bridge.serviceId]?.metadataId];
        for (const node in nodes) {
            if (nodes?.[node]?.restApiName === subAppApi) {
                return nodes?.[node];
            }
        }
    }
);

export const getEninvoiceSelectedApp$ = createSelector(
    getEninvoiceSelectedPackageRestApiName$,
    getEninvoiceSelectedBridgeRestApiName$,
    getEninvoiceSelectedAppRestApiName$,
    getEninvoicePackagedApps$,
    getEninvoiceSubscribedAppsApiMap$,
    getEninvoiceSelectedBridgeNode$,
    getAssetsWithLatestVersion$,
    (packageApi, bridgeApi, appApi, packagedApps, appsMap, bridgeNode, version) => {
        if (version?.[appsMap?.[appApi]?.serviceId]?.metadataId) {
            return {
                ...(packageApi
                    ? packagedApps?.find(app => app?.restApiName === appApi)
                    : bridgeApi
                    ? (bridgeNode as any as AssetService)
                    : appsMap?.[appApi]),
                assetMetaUId: version?.[appsMap?.[appApi]?.serviceId]?.metadataId,
            } as AssetService;
        }
        return (
            packageApi
                ? packagedApps?.find(app => app?.restApiName === appApi)
                : bridgeApi
                ? (bridgeNode as any as AssetService)
                : appsMap?.[appApi]
        ) as AssetService;
    }
);

export const getEninvoiceSelectedBridgeReport$ = createSelector(
    getBridgeNodesMap$,
    getEninvoiceSelectedBridgeApp$,
    getEninvoiceSelectedReportName$,
    getAssetsWithLatestVersion$,
    (nodesMap, bridge, reportName = '', version) => {
        const bridgeAssetMetaUId = bridge?.assetMetaUId || '';
        const versionMetadataId = version?.[bridge?.serviceId]?.metadataId || '';
        if (reportName?.length === 0 && (bridgeAssetMetaUId?.length === 0 || versionMetadataId?.length === 0)) {
            return;
        }
        const nodes = nodesMap?.[bridgeAssetMetaUId || versionMetadataId];
        for (const node in nodes) {
            if (nodes?.[node]?.name === reportName) {
                return nodes?.[node];
            }
        }
    }
);

export const getEninvoiceBridgeMetaData$ = createSelector(
    getAppsMetaDataMap$,
    getAppsTemplate$,
    getEninvoiceSelectedBridgeApp$,
    getCurrentOrganizationId$,
    getAssetsWithLatestVersion$,
    (metaDataMap, appsTemplateMap, app, orgId, version) =>
        transformEninvoiceMetaData(
            metaDataMap,
            appsTemplateMap,
            app?.serviceId,
            app?.assetMetaUId || version?.[app?.serviceId]?.metadataId,
            orgId
        )
);

export const getEninvoiceAppMetaData$ = createSelector(
    getAppsMetaDataMap$,
    getAppsTemplate$,
    getEninvoiceSelectedApp$,
    getCurrentOrganizationId$,
    getAssetsWithLatestVersion$,
    (metaDataMap, appsTemplateMap, app, orgId, versionsMap) =>
        transformEninvoiceMetaData(metaDataMap, appsTemplateMap, app?.serviceId, app?.assetMetaUId, orgId, versionsMap?.[app?.serviceId])
);

const transformEninvoiceMetaData = (
    metaDataMap: {
        [property: string]: {
            [property: string]: AssetData;
        };
    },
    appsTemplateMap: {
        [property: string]: {
            isOfCurrentOrg: boolean;
            uiTemplate: AppTemplate;
        };
    },
    serviceId: string,
    assetId: string,
    orgId: string,
    version?: AppsState['assetsWithLatestVersion']['serviceId']
) => {
    const metaId = assetId || version?.metadataId;
    if (!metaId) {
        return;
    }
    const metaData = transformMetaDataWithTemplate(metaDataMap, appsTemplateMap, serviceId, metaId);
    const appData = transformAppMetaData(metaData?.restAPIName, metaData);
    if (appData && orgId) {
        appData.organizationId = orgId;
    }
    return appData;
};

const getEninvoiceInstanceIds$ = createSelector(
    getInstancesOrgServicesMap$,
    getEninvoiceSelectedApp$,
    (instancesMap, app) => instancesMap?.[app?.serviceId]?.searchedInstances?.['eninvoice']
);

export const getEninvoiceInstancesPagination$ = createSelector(
    getInstancesOrgServicesMap$,
    getEninvoiceSelectedApp$,
    getEninvoiceInstanceIds$,
    (instancesMap, app, instanceIds) => {
        const page = instancesMap?.[app?.serviceId]?.pagination || { size: 0, pageIndex: 0, pagingState: undefined };
        const fetchedCount = instanceIds?.length || 0;
        const pageCount = (page?.pageIndex + 1) * page?.size;
        const count = (page?.pagingState ? pageCount : fetchedCount) + (page?.pagingState !== undefined ? 1 : 0);
        return {
            ...page,
            count,
        };
    }
);

const getEninvoicePaginatedInstanceIds$ = createSelector(
    getEninvoiceInstancesPagination$,
    getEninvoiceInstanceIds$,
    (pagination, instanceIds) => {
        const start = (pagination?.pageIndex || 0) * (pagination?.size || 0);
        const end = ((pagination?.pageIndex || 0) + 1) * (pagination?.size || 0);
        return instanceIds?.filter((_instanceId, index) => index >= start && index < end);
    }
);

const getEninvoiceInstances$ = createSelector(
    getServicesInstancesMap$,
    getEninvoicePaginatedInstanceIds$,
    getCurrentOrganizationId$,
    (instancesMap, instanceIds, currentOrganizationId) =>
        location.href?.includes(currentOrganizationId) && instancesMap?.[instanceIds?.[0]]?.unitId === currentOrganizationId
            ? instanceIds?.reduce((instances, id) => {
                  instances.push(instancesMap?.[id]);
                  return instances;
              }, [] as Instance[])
            : []
);

export const getEninvoiceConvertedInstances$ = createSelector(getEninvoiceInstances$, instances => {
    return (
        instances?.reduce(
            (records, instance) => {
                const record = {
                    id: undefined,
                    assetDataId: instance?.assetDataId,
                    assetId: instance?.assetId,
                    fields: [],
                    grids: [],
                    errors: [],
                    warnings: [],
                    instanceState: instance?.instanceState,
                    requestId: instance?.assetRequests?.[0]?.requestId,
                    createdOn: instance?.createdOn,
                };
                if (!instance) {
                    return records;
                }
                const valuesMap = {
                    ...instance?.processViewAttributes,
                };
                Object.keys(instance?.attributesMap).forEach(attr => {
                    instance?.attributesMap[attr]?.list?.forEach(field => {
                        valuesMap[field?.name?.split('^')[1]] = field.value;
                    });
                });
                Object.keys(valuesMap).forEach(id => {
                    record.fields.push({ id, value: valuesMap?.[id] });
                });
                records.push(record as any);
                return records;
            },
            [] as {
                id: string;
                fields: {
                    id: string;
                    value: any;
                }[];
                grids: [];
                errors: string[];
                warnings: string[];
                assetDataId: string;
                instanceState?: string;
                requestId?: string;
                createdOn?: string;
            }[]
        ) || []
    );
});

export const getEninvoiceSelectedInstance$ = createSelector(
    getServicesInstancesMap$,
    getEninvoiceSelectedInstanceId$,
    (map, instanceId) => map?.[instanceId]
);

export const getEninvoiceConvertedInstance$ = createSelector(getEninvoiceSelectedInstance$, instance => {
    if (!instance) {
        return;
    }
    const record = {
        id: undefined,
        assetDataId: instance.assetDataId,
        fields: [],
        grids: [],
        errors: [],
        warnings: [],
        instanceState: instance?.instanceState,
    };

    const valuesMap = {
        ...instance?.processViewAttributes,
    };

    Object.keys(instance.attributesMap || {}).forEach(attr => {
        instance.attributesMap[attr]?.list?.forEach(field => {
            valuesMap[field?.name?.split('^')[1]] = field.value;
        });
    });

    Object.keys(valuesMap || {}).forEach(id => {
        record.fields.push({ id, value: valuesMap?.[id] });
    });
    return record as any as Record;
});

export const getEninvoiceInstanceMetaData$ = createSelector(
    getAppsMetaDataMap$,
    getAppsTemplate$,
    getEninvoiceSelectedApp$,
    getEninvoiceSelectedInstance$,
    getCurrentOrganizationId$,
    (metaDataMap, appsTemplateMap, app, instance, orgId) =>
        transformEninvoiceMetaData(metaDataMap, appsTemplateMap, app?.serviceId, instance?.assetId, orgId)
);

export const getEninvoiceSelectedInstanceReports$ = createSelector(
    getReportsMap$,
    getEninvoiceSelectedInstanceId$,
    (reportsMap, selectedInstanceId) => reportsMap?.[selectedInstanceId] || []
);

export const getEninvoiceSelectedInstanceNotifications$ = createSelector(
    getEninvoiceSelectedInstanceId$,
    getNotificationsMap$,
    (instaceId, notificationsMap) => {
        const instanceNotifications = notificationsMap?.[instaceId];
        return (instanceNotifications?.ids as string[])?.reduce((events, id) => {
            events.push(instanceNotifications?.entities?.[id]);
            return events;
        }, [] as NotificationEvent[]);
    }
);

export const getEninvoiceSearchReportsByServiceId$ = createSelector(
    getEninvoiceSelectedApp$,
    getSearchReportsOrgMapByCurrentOrgId$,
    (app, searchReportsServiceMap) => searchReportsServiceMap?.[app?.serviceId]
);

export const getEninvoiceSearchReports$ = createSelector(
    getEninvoiceSearchReportsByServiceId$,
    searchReportsService => searchReportsService?.searchReports
);

export const getEninvoiceSearchReportsPagination$ = createSelector(getEninvoiceSearchReportsByServiceId$, searchReportsService => {
    const pagination = searchReportsService?.searchReportsPagination;
    const count = searchReportsService?.searchReports?.length || 0;
    return {
        count,
        pageIndex: pagination?.pageIndex || 0,
        pageSize: pagination?.pageSize || 10,
        pagingState: pagination?.pagingState,
        timeRange: pagination?.timeRange || [],
    };
});

export const getEninvoiceSelectedPageSearchReports$ = createSelector(
    getEninvoiceSearchReportsPagination$,
    getEninvoiceSearchReports$,
    (pagination, allSearchReports) => {
        const start = pagination.pageIndex * pagination.pageSize;
        const end = (pagination.pageIndex + 1) * pagination.pageSize;
        return allSearchReports?.filter((_report, index) => index >= start && index < end);
    }
);

export const getEninvoiceOutboundChains$ = createSelector(
    getEninvoiceSelectedApp$,
    getAllTransformationsServicesMap$,
    (app, map) => map?.[app?.restApiName]?.transformations?.chains || []
);

export const getEninvoiceDownloadableSearchReport$ = createSelector(
    getEninvoiceSearchReportsByServiceId$,
    searchReportsService => searchReportsService?.downloadedSearchReports
);

export const getEninvoiceSelectedInstanceWorkFlow$ = createSelector(
    getWorkflowInstances$,
    getEninvoiceSelectedInstanceId$,
    getEninvoiceInstanceMetaData$,
    (workflowMap, selectedInstanceId, metaData) => {
        const stages = metaData?.workflowMetadata?.stages;
        // Need to update display name
        const workflows = CommonUtilsService.cloneObject(workflowMap?.[selectedInstanceId]);
        workflows?.forEach(workflow => {
            const stage = stages?.find(stage => stage.id === workflow.taskDefinitionKey);
            workflow.displayName = stage?.displayName || workflow.displayName;
        });
        return workflows || [];
    }
);

export const getEninvoiceCurrentWorkflow$ = createSelector(getEninvoiceSelectedInstanceWorkFlow$, workflows => {
    let currentlySelectedWorkflow = workflows?.find(workflow => (workflow.startTime && !workflow.endTime) || workflow.cancelled);
    if (!currentlySelectedWorkflow) {
        currentlySelectedWorkflow = workflows
            .slice(0)
            .reverse()
            .find(rule => rule.startTime?.length > 0 && rule.endTime?.length > 0);
    }
    return workflows === undefined ? undefined : workflows.length ? currentlySelectedWorkflow : {};
});

export const getEninvoiceWorkflowLoadingStatus$ = createSelector(
    getWorkflowsLoadingMap$,
    getEninvoiceSelectedInstanceId$,
    getEninvoiceInstanceMetaData$,
    getEninvoiceCurrentWorkflow$,
    (loadingMap, instanceId, metaData, stage) => loadingMap?.[instanceId] || (metaData?.workflowMetadata?.stages?.length > 0 && !stage)
);

const getEninvoiceRequestsIds$ = createSelector(
    getRequestsOrgServicesMap$,
    getEninvoiceSelectedApp$,
    getSelectedRequestLogName$,
    (requestsMap, app, requestLogName) => requestsMap?.[app?.serviceId]?.searchedRequestIds?.[requestLogName]
);

export const getEninvoiceRequestsPagination$ = createSelector(
    getRequestsOrgServicesMap$,
    getEninvoiceSelectedApp$,
    getEninvoiceRequestsIds$,
    (requestsMap, app, requestIds) => {
        const page = requestsMap?.[app?.serviceId]?.pagination || { pageSize: 0, pageIndex: 0, pagingState: undefined, timeRange: [] };
        const fetchedCount = requestIds?.length || 0;
        const pageCount = (page?.pageIndex + 1) * page?.pageSize;
        const count = (page?.pagingState ? pageCount : fetchedCount) + (page?.pagingState !== undefined ? 1 : 0);
        return {
            ...page,
            count,
        };
    }
);

export const getEninvoicePaginatedRequestIds$ = createSelector(
    getEninvoiceRequestsPagination$,
    getEninvoiceRequestsIds$,
    (pagination, requestIds) => {
        const start = (pagination?.pageIndex || 0) * (pagination?.pageSize || 0);
        const end = ((pagination?.pageIndex || 0) + 1) * (pagination?.pageSize || 0);
        return requestIds?.filter((_requestId, index) => index >= start && index < end);
    }
);

export const getEninvoiceRequests$ = createSelector(getRequests$, getEninvoicePaginatedRequestIds$, (requestsMap, requestIds) => {
    return requestIds?.reduce((requests, id) => {
        const request: any = requestsMap?.[id];
        requests.push(request);
        return requests;
    }, [] as Request[] | ReconciliationRequest[]);
});
